import React, {
  useState,
  useRef,
  useEffect,
  useContext,
  useCallback,
} from 'react';
import bellTokens from '@amzn/meridian-tokens/base/icon/bell';
import Responsive from '@amzn/meridian/responsive';
import Badge from '@amzn/meridian/badge';
import Button from '@amzn/meridian/button';
import Popover, { PopoverHeader } from '@amzn/meridian/popover';
import Icon from '@amzn/meridian/icon';
import Row from '@amzn/meridian/row';
import Heading from '@amzn/meridian/heading';
import Tag from '@amzn/meridian/tag';
import { NoNotifications } from './NoNotifications';
import { InformationalNotification } from './InformationalNotification';
import { NavigationalNotification } from './NavigationalNotification';
import { FeedbackNotification } from './FeedbackNotification';
import { StateContext } from '../../GlobalStateStore';
import DataSource from '../../api';
import Table, { TableCell, TableRow } from '@amzn/meridian/table';
import Box from '@amzn/meridian/box';
import moment from 'moment';
import DataState from '../../Types/DataState';
import {
  StatusObject,
  ChannelObject,
  StatusInputObject,
  Status,
  NotificationMetadata,
  Content,
} from '../../api/Notifications/schema';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import { AppNotification } from '../../api/Notifications/schema';
import { processNotifLocaleStrings } from '../../Util/NotificationsHelper';
import Column from '@amzn/meridian/column';
import Divider from '@amzn/meridian/divider';
import { Feature, isFeatureEnabled } from '../../config/FeaturesConfig';

export enum ContentType {
  LOCALIZED = 'LOCALIZED',
  DEFAULT = 'DEFAULT',
}

export const NotificationsPopover: React.FC = (): React.ReactElement => {
  const intl: IntlShape = useIntl();
  const apiNaws = DataSource.getDataFeed('NAWS');
  // App notifications from backend
  const [notificationsFromQuery, setNotificationsFromQuery] = useState<
    AppNotification[]
  >([]);
  // Visible notifications
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  // Unread notifications
  const [sentNotifications, setSentNotifications] = useState<AppNotification[]>(
    []
  );
  const [open, setOpen] = useState<boolean>(false);

  // Notification constants - stays the same for all the env. No need to process.env
  const width = 600;
  const pollInterval = 1000 * 60 * 20;
  const clientName = 'Inspire';

  const globalState = useContext(StateContext);
  const { inspireOptions } = globalState.state;
  const { userID, requesterUsername, requesterOwner } = inspireOptions.data;

  const outlierManagement = isFeatureEnabled(Feature.OUTLIER_MANAGEMENT);

  const onOpen = () => {
    const updatedNotifications: AppNotification[] = notifications.map(
      (notification: AppNotification) => {
        if (notification.status === Status.SENT) {
          markReadNotificationMutation(notification.notificationId);
        }
        return notification;
      }
    );
    setOpen(true);
  };

  const onClose = () => {
    const updatedNotifications: AppNotification[] = notifications.map(
      (notification: AppNotification) => {
        if (notification.status === Status.SENT) {
          return {
            ...notification,
            status: Status.READ,
          };
        }
        return notification;
      }
    );
    setOpen(false);
    setNotifications(updatedNotifications);
  };

  const onClickSeeAll = () => {
    console.log('See All'); // when we enable "see all" feature
  };

  const onLoad = (val: string) => {
    console.log(val);
  };

  const submitEvaluationMutation = async (
    notificationId: string,
    evaluationData: string
  ) => {
    const updatedNotifications: AppNotification[] = notifications.filter(
      (notification: AppNotification) => {
        return notification.notificationId !== notificationId;
      }
    );
    const notification: AppNotification | undefined = notifications.find(
      notif => notif.notificationId === notificationId
    );
    const variables = apiNaws.prepareQueryForUpdateNotification({
      clientName: clientName,
      status: StatusInputObject.DISMISSED,
      notificationId: notificationId,
      metadata: JSON.stringify({
        ...(notification &&
          notification.metadata &&
          JSON.parse(notification.metadata)),
        evaluationData: evaluationData,
      }),
    });
    apiNaws.updateAppNotification(variables, onLoad, onLoad);
    setNotifications(updatedNotifications);
  };

  const dismissNotificationMutation = async (notificationId: string) => {
    const variables = apiNaws.prepareQueryForUpdateNotification({
      clientName: clientName,
      status: StatusInputObject.DISMISSED,
      notificationId: notificationId,
    });
    apiNaws.updateAppNotification(variables, onLoad, onLoad);
  };

  const markReadNotificationMutation = async (notificationId: string) => {
    const variables = apiNaws.prepareQueryForUpdateNotification({
      clientName: clientName,
      status: StatusInputObject.READ,
      notificationId: notificationId,
    });
    apiNaws.updateAppNotification(variables, onLoad, onLoad);
  };

  const handleDismiss = (id: string) => {
    const updatedNotifications: AppNotification[] = notifications.filter(
      (notification: AppNotification) => {
        return notification.notificationId !== id;
      }
    );
    dismissNotificationMutation(id);
    setNotifications(updatedNotifications);
  };

  const getNotification = useCallback(() => {
    if (userID === '' || inspireOptions.state === DataState.LOADING) {
      return;
    }
    const variables = apiNaws.prepareQueryForGetNotification({
      windowStart: moment().utc().subtract(1, 'month').format(),
      windowEnd: moment().utc().format(),
      target: `${requesterUsername}@${requesterOwner}`.toLowerCase(),
      channel: ChannelObject.APP,
      statusCodes: [StatusObject.READ, StatusObject.SENT],
      clientName: clientName,
    });
    apiNaws.getAppNotifications(variables, setNotificationsFromQuery, onLoad);
  }, [userID]);

  useEffect(() => {
    getNotification();
  }, [userID]);

  // Add additional conditions inside this function to filter out notifications
  const outlierNotificationFilter = useCallback(
    (notification: AppNotification) => {
      const metadata: NotificationMetadata = notification.metadata
        ? JSON.parse(notification.metadata)
        : {};
      return metadata?.notificationType !== 'OUTLIER' || outlierManagement;
    },
    [outlierManagement]
  );

  useEffect(() => {
    setNotifications(notificationsFromQuery.filter(outlierNotificationFilter));
  }, [notificationsFromQuery]);

  useEffect(() => {
    const sentNotifications = notifications.filter(
      notif => notif.status === Status.SENT
    );
    setSentNotifications(sentNotifications);
  }, [notifications]);

  const savedCallback = useRef(() =>
    console.log(`Notification polling interval set to ${pollInterval} ms`)
  );

  useEffect(() => {
    if (userID === '' || inspireOptions.state === DataState.LOADING) {
      return;
    }
    savedCallback.current = getNotification;
  }, [getNotification]);

  useEffect(() => {
    function notificationTimer() {
      savedCallback.current();
    }
    const id = setInterval(notificationTimer, pollInterval);
    return () => {
      clearInterval(id);
    };
  }, [pollInterval, userID]);

  const renderNotification = (notification: AppNotification) => {
    const message = notification.message;
    const notificationMetadata: NotificationMetadata = notification.metadata
      ? JSON.parse(notification.metadata)
      : {};
    const messageBody = message.body;
    const metadata: Record<string, any> = messageBody.metadata
      ? JSON.parse(messageBody.metadata)
      : {};
    const content: Content = messageBody.content
      ? JSON.parse(messageBody.content)
      : {};
    let notificationComponent: undefined | React.ReactNode = undefined;
    if (content.nav) {
      notificationComponent = createNavigationalNotification(
        notification,
        content,
        metadata,
        notificationMetadata
      );
    } else if (content.feedback) {
      notificationComponent = createFeedbackNotification(
        notification,
        content,
        metadata,
        notificationMetadata
      );
    } else if (!content.nav && !content.feedback) {
      notificationComponent = createInformationalNotification(
        notification,
        content,
        metadata,
        notificationMetadata
      );
    }
    return notification.status && notificationComponent ? (
      <React.Fragment key={notification.notificationId}>
        <Divider />
        <TableRow>
          <TableCell
            columnSpan={content.nav || content.feedback ? 1 : undefined}
          >
            <Column spacing='300' maxWidth={width - 50} minWidth={width - 50}>
              {notificationComponent}
            </Column>
          </TableCell>
        </TableRow>
        <Divider />
      </React.Fragment>
    ) : null;
  };

  const createNavigationalNotification = (
    notification: AppNotification,
    content: Content,
    metadata: Record<string, any>,
    notificationMetadata: NotificationMetadata
  ) => {
    return (
      <NavigationalNotification
        key={notification.notificationId}
        externalId={notification.notificationId}
        title={processNotifLocaleStrings(
          intl,
          content.contentType,
          content.title,
          metadata
        )}
        message={processNotifLocaleStrings(
          intl,
          content.contentType,
          content.message,
          metadata
        )}
        text={processNotifLocaleStrings(
          intl,
          content.contentType,
          content.nav?.text ?? '-',
          metadata
        )}
        link={
          content.nav?.link
            ? processNotifLocaleStrings(
                intl,
                ContentType.DEFAULT,
                content.nav.link,
                metadata
              )
            : undefined
        }
        relativeLink={
          content.nav?.relativeLink
            ? processNotifLocaleStrings(
                intl,
                ContentType.DEFAULT,
                content.nav.relativeLink,
                metadata
              )
            : undefined
        }
        timestamp={notification.sentTime}
        dismissable={content.dismissable ?? false}
        handleDismiss={handleDismiss}
        onClose={onClose}
        notificationMetadata={notificationMetadata}
        status={notification.status}
      />
    );
  };

  const createFeedbackNotification = (
    notification: AppNotification,
    content: Content,
    metadata: Record<string, any>,
    notificationMetadata: NotificationMetadata
  ) => {
    return (
      <FeedbackNotification
        key={notification.notificationId}
        externalId={notification.notificationId}
        title={processNotifLocaleStrings(
          intl,
          content.contentType,
          content.title,
          metadata
        )}
        message={processNotifLocaleStrings(
          intl,
          content.contentType,
          content.message,
          metadata
        )}
        options={(content.feedback?.options ?? []) as { option: string }[]}
        contentType={content.contentType}
        metadata={metadata}
        timestamp={notification.sentTime}
        dismissable={content.dismissable ?? false}
        notificationMetadata={notificationMetadata}
        handleDismiss={handleDismiss}
        submitEvaluation={submitEvaluationMutation}
        status={notification.status}
      />
    );
  };

  const createInformationalNotification = (
    notification: AppNotification,
    content: Content,
    metadata: Record<string, any>,
    notificationMetadata: NotificationMetadata
  ) => {
    return (
      <InformationalNotification
        key={notification.notificationId}
        externalId={notification.notificationId}
        title={processNotifLocaleStrings(
          intl,
          content.contentType,
          content.title,
          metadata
        )}
        message={processNotifLocaleStrings(
          intl,
          content.contentType,
          content.message,
          metadata
        )}
        timestamp={notification.sentTime}
        dismissable={content.dismissable ?? false}
        notificationMetadata={notificationMetadata}
        handleDismiss={handleDismiss}
        status={notification.status}
      />
    );
  };

  const buttonRef = useRef();
  return (
    <Responsive
      query='min-width'
      props={{
        isMobileView: {
          default: true,
          '10px': false, // TODO - change it to 700px for Notification centre tab feature release
        },
      }}
    >
      {({ isMobileView }) => (
        <Row alignmentHorizontal='end'>
          <Button
            type='icon'
            size='small'
            onClick={isMobileView ? () => onClickSeeAll() : () => onOpen()}
            ref={buttonRef}
            data-csa-c-type='button'
            data-csa-c-slot-id='click-on-notification-bell-slot'
            data-csa-c-content-id='click-on-notification-bell'
            data-csa-c-description='Click on Notification Bell icon'
            data-csa-c-tag1='Notifications'
          >
            <Icon tokens={bellTokens}>
              {sentNotifications.length > 0 && (
                <Badge value={sentNotifications.length} type='error' />
              )}
            </Icon>
          </Button>
          {!isMobileView && (
            <Popover
              anchorNode={buttonRef.current}
              open={open}
              onClose={onClose}
              position='bottom'
              minWidth={width}
              maxWidth={width}
              alignment='center'
            >
              <PopoverHeader>
                {/* "Magic number" width calculation required to position right-aligned link */}
                <Row spacing='500' width={width - 64} widths={['fill', 'fit']}>
                  <Row spacing='300'>
                    <Heading level={2} type='h200'>
                      <FormattedMessage
                        id='cs_perfportal_notifications_popup_title'
                        defaultMessage='Notifications'
                        description='Title for notifications popover'
                      />
                    </Heading>
                    {sentNotifications.length > 0 && (
                      <Tag>
                        {intl.formatMessage(
                          {
                            id: 'cs_perfportal_notifications_popup_new',
                            defaultMessage: '{number} New',
                            description: 'Number of new notifications',
                          },
                          {
                            number: sentNotifications.length,
                          }
                        )}
                      </Tag>
                    )}
                  </Row>
                </Row>
              </PopoverHeader>
              {!notifications.length ? (
                <NoNotifications />
              ) : (
                <Box maxHeight='75vh' overflowY='auto'>
                  <Table showDividers={true} spacing='medium'>
                    {notifications.map((notification: AppNotification) => {
                      return renderNotification(notification);
                    })}
                  </Table>
                </Box>
              )}
            </Popover>
          )}
        </Row>
      )}
    </Responsive>
  );
};
